<template>
  <div class="container counterparty-details-container">
    <base-loader v-if="dataIsLoading" />
    <div
      class="counterparty-block"
      v-else-if="!dataIsLoading && counterparty"
    >
      <base-nav
        :title="counterparty.name"
        :show-back="true"
        back-fallback="Counterparties"
        :back-fallback-pattern="/\/counterparties/"
      />

      <div class="card counterparty-main">
        <CounterpartyMainInfo
          :counterparty="counterparty"
          :editing-counterparty-info="editingCounterpartyInfo"
          :loading="loading"
          @start-edit="editingCounterpartyInfo = true"
          @toggle-status="toggleStatus"
          v-if="!editingCounterpartyInfo"
        />

        <div class="counterparty-edit" v-else>
          <CounterpartyCreateForm
            :formData="formData"
            @update:formData="updateFormData"
            ref="formRef"
          />

          <div class="form-actions">
            <base-primary-button
              class="form-btn"
              type="submit"
              :disabled="isSubmitting"
              @click="updateCounterparty"
            >
              <span v-if="isSubmitting">Сохранение...</span>
              <span v-else>Сохранить</span>
            </base-primary-button>

            <base-button
              class="form-btn"
              type="button"
              :disabled="isSubmitting"
              @click="handleCancel"
            >
              Отмена
            </base-button>
          </div>
        </div>
      </div>

      <RequisiteList
        class="counterparty-requisite-block"
        :id="id"
        :allowed_models="['Counterparty']"
        :requisitable_type="'Counterparty'"
      />
    </div>
    <div class="no-data" v-else>Контрагент не найден</div>
  </div>
</template>

<script>
import { formatDateAndTime } from "@/utils/dateUtils";
import { getCompanyId } from "@/utils/userUtils";

import counterpartiesApi from "@/services/api/counterpartiesApi";

import CounterpartyMainInfo from "@/components/Counterparty/CounterpartyMainInfo.vue";
import CounterpartyCreateForm from "@/components/Counterparty/CounterpartyCreateForm.vue";
import RequisiteList from "@/components/Requisite/RequisiteList.vue";

import { useNotificationStore } from "@/stores/notificationStore";

export default {
  name: "CounterpartyDetailsPage",
  components: {
    CounterpartyMainInfo,
    CounterpartyCreateForm,
    RequisiteList,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      companyId: getCompanyId(),

      dataIsLoading: true,
      isSubmitting: false,
      loading: false,
      editingCounterpartyInfo: false,

      counterparty: {},
      formData: {
        name: "",
        inn: "",
        kpp: "",
        phone: "",
        email: "",
        city: "",
        street: "",
        house: "",
        legal_address: "",
        postal_address: "",
        postal_code: "",
        region_code: "",
        bank_name: "",
        bik: "",
        account_number: "",
        correspondent_account: "",
      },
    };
  },
  watch: {
    id() {
      this.getCounterpartyRequest();
    },
  },
  computed: {
    notificationStore() {
      return useNotificationStore();
    },
  },
  methods: {
    createFormData() {
      this.formData = {
        name: this.counterparty.name || "",
        inn: this.counterparty.inn || "",
        kpp: this.counterparty.kpp || "",
        phone: this.counterparty.phone || "",
        email: this.counterparty.email || "",
        city: this.counterparty.city || "",
        street: this.counterparty.street || "",
        house: this.counterparty.house || "",
        legal_address: this.counterparty.legal_address || "",
        postal_address: this.counterparty.postal_address || "",
        postal_code: this.counterparty.postal_code || "",
        region_code: this.counterparty.region_code || "",
        bank_name: this.counterparty.bank_name || "",
        bik: this.counterparty.bik || "",
        account_number: this.counterparty.account_number || "",
        correspondent_account:
          this.counterparty.correspondent_account || "",
      };
    },

    updateFormData(newData) {
      Object.assign(this.formData, newData);
    },

    async getCounterpartyRequest() {
      this.dataIsLoading = true;

      try {
        const response = await counterpartiesApi.show(
          this.companyId,
          this.id
        );

        this.counterparty = response.data.result;

        this.createFormData();
      } finally {
        this.dataIsLoading = false;
      }
    },

    async updateCounterparty() {
      const formComponent = this.$refs.formRef;

      let isFormValid = true;
      if (
        formComponent &&
        typeof formComponent.validate === "function"
      ) {
        isFormValid = await formComponent.validate();
      }

      if (!isFormValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля корректно"
        );
        return;
      }

      try {
        this.isSubmitting = true;

        const payload = {
          name: this.formData.name,
          inn: this.formData.inn,
          kpp: this.formData.kpp,
          phone: this.formData.phone,
          email: this.formData.email,
          city: this.formData.city,
          street: this.formData.street,
          house: this.formData.house,
          legal_address: this.formData.legal_address,
          postal_address: this.formData.postal_address,
          postal_code: this.formData.postal_code,
          region_code: this.formData.region_code,
          bank_name: this.formData.bank_name,
          bik: this.formData.bik,
          account_number: this.formData.account_number,
          correspondent_account: this.formData.correspondent_account,
        };

        const response = await counterpartiesApi.update(
          this.companyId,
          this.id,
          payload
        );

        this.counterparty = response.data.result;

        this.notificationStore.showSuccess(
          "Контрагент успешно обновлён"
        );

        this.editingCounterpartyInfo = false;
      } catch (error) {
        console.error("Ошибка при обновлении контрагента:", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    async toggleStatus() {
      this.loading = true;

      try {
        let response;

        if (this.counterparty.enabled) {
          response = await counterpartiesApi.disable(
            this.companyId,
            this.counterparty.id
          );

          this.notificationStore.showSuccess("Контрагент отключен");
        } else {
          response = await counterpartiesApi.enable(
            this.companyId,
            this.counterparty.id
          );

          this.notificationStore.showSuccess("Контрагент включен");
        }

        this.counterparty = response.data.result;
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.createFormData();
      this.editingCounterpartyInfo = false;
    },

    formatDateAndTime,
  },
  async created() {
    await this.getCounterpartyRequest();
  },
};
</script>

<style scoped>
.form-actions,
.counterparty-requisite-block {
  margin-top: var(--base-out-indent);
}
</style>
